/*
 * OperatorSelect — selector del operador para el branding del SAR.
 *
 * Lista los operadores predefinidos (TOTEM, ORANGE, ...) devueltos por
 * `GET /api/research/sar/operators`, y permite además añadir un operador CUSTOM
 * con nombre, color (hex) y logo PNG opcional (upload → base64).
 *
 * Props:
 *   value: null | { id?, name?, color?, logo_b64? }
 *   onChange(next: null | {id?,name?,color?,logo_b64?}): void
 *   compact?: boolean  (versión más pequeña para modales apretados)
 */
import React, { useEffect, useState, useRef } from 'react';
import api from '../../api/client';
import { Upload, X, Plus, Check } from 'lucide-react';

const HEX_RE = /^#?[0-9A-Fa-f]{6}$/;

const OperatorSelect = ({ value, onChange, compact = false }) => {
  const [ops, setOps] = useState([]);
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customColor, setCustomColor] = useState('#0A0A0A');
  const [customLogoB64, setCustomLogoB64] = useState('');
  const [customLogoPreview, setCustomLogoPreview] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/research/sar/operators').then(({ data }) => setOps(data || [])).catch(() => setOps([]));
  }, []);

  // Cuando se selecciona un operador predefinido, cierra el editor custom
  const selectPredef = (op) => {
    setShowCustom(false);
    onChange({ id: op.id });
  };

  const isSelected = (op) => value?.id === op.id;
  const isCustom = value && !value.id && value.name;

  const handleLogo = async (file) => {
    if (!file) return;
    if (file.size > 512 * 1024) {
      alert('El logo debe pesar menos de 500KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = String(dataUrl).split(',')[1] || '';
      setCustomLogoB64(b64);
      setCustomLogoPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const saveCustom = () => {
    if (!customName.trim()) { alert('Introduce el nombre del operador'); return; }
    const color = customColor.trim();
    if (color && !HEX_RE.test(color)) { alert('El color debe ser hexadecimal (ej. #E4002B)'); return; }
    onChange({
      name: customName.trim(),
      color: color.startsWith('#') ? color : `#${color}`,
      logo_b64: customLogoB64 || null,
    });
    setShowCustom(false);
  };

  const clear = () => { onChange(null); setShowCustom(false); };

  const btnSize = compact ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[11px]';

  return (
    <div className="space-y-2" data-testid="operator-select">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase text-white/50 font-bold">Operador (opcional)</span>
        {value && (
          <button type="button" onClick={clear} className="text-white/40 hover:text-white text-[10px] flex items-center gap-1"
            data-testid="operator-clear">
            <X size={11}/> Quitar
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {ops.map((op) => (
          <button
            key={op.id}
            type="button"
            onClick={() => selectPredef(op)}
            data-testid={`operator-btn-${op.id}`}
            className={`${btnSize} rounded-md font-black tracking-wide border transition-colors flex items-center gap-1.5 ${
              isSelected(op)
                ? 'border-white text-white shadow-[0_0_0_2px_rgba(163,255,18,0.4)]'
                : 'border-white/10 hover:border-white/40 text-white/90'
            }`}
            style={op.logo_data_uri
              ? { backgroundColor: '#FFFFFF', color: '#111' }
              : { backgroundColor: op.color, color: op.text_color }}
          >
            {isSelected(op) && <Check size={10}/>}
            {op.logo_data_uri
              ? <img src={op.logo_data_uri} alt={op.name} className="h-4 max-w-[80px] object-contain"/>
              : <span>{op.name}</span>}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setShowCustom((s) => !s)}
          data-testid="operator-custom-toggle"
          className={`${btnSize} rounded-md font-black tracking-wide border border-dashed transition-colors ${
            isCustom
              ? 'border-[#A3FF12] text-[#A3FF12] bg-[#A3FF12]/10'
              : 'border-white/20 text-white/60 hover:border-white/40 hover:text-white'
          }`}
        >
          <Plus size={10} className="inline mr-1"/>
          {isCustom ? `Custom: ${value.name}` : 'Añadir operador…'}
        </button>
      </div>

      {showCustom && (
        <div className="bg-black/40 border border-white/10 rounded-lg p-3 space-y-2" data-testid="operator-custom-form">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase text-white/50 font-bold">Nombre</span>
              <input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Ej. CELLNEX"
                data-testid="operator-custom-name"
                className="mt-1 w-full bg-black border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-[#A3FF12]/50"/>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase text-white/50 font-bold">Color hex</span>
              <div className="flex gap-1 mt-1">
                <input
                  type="color"
                  value={HEX_RE.test(customColor.replace('#','')) ? (customColor.startsWith('#') ? customColor : `#${customColor}`) : '#0A0A0A'}
                  onChange={(e) => setCustomColor(e.target.value.toUpperCase())}
                  className="w-8 h-8 rounded cursor-pointer bg-black border border-white/10"
                  data-testid="operator-custom-color-picker"/>
                <input
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  placeholder="#0A0A0A"
                  data-testid="operator-custom-color"
                  className="flex-1 bg-black border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-[#A3FF12]/50"/>
              </div>
            </label>
          </div>

          <div>
            <span className="text-[10px] uppercase text-white/50 font-bold">Logo (opcional, PNG · &lt;500KB)</span>
            <div className="flex items-center gap-2 mt-1">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
                data-testid="operator-custom-logo-input"
                onChange={(e) => handleLogo(e.target.files?.[0])}/>
              <button type="button" onClick={() => fileRef.current?.click()}
                data-testid="operator-custom-logo-btn"
                className="flex items-center gap-1 bg-white/5 border border-white/10 hover:bg-white/10 text-white text-[11px] font-bold px-2 py-1.5 rounded">
                <Upload size={11}/> Subir logo
              </button>
              {customLogoPreview && (
                <div className="flex items-center gap-1">
                  <img src={customLogoPreview} alt="logo preview" className="h-8 max-w-[80px] bg-white rounded px-1"/>
                  <button type="button" onClick={() => { setCustomLogoB64(''); setCustomLogoPreview(''); }}
                    className="text-white/40 hover:text-white p-1" title="Quitar logo">
                    <X size={11}/>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowCustom(false)}
              className="text-white/50 hover:text-white text-[11px] px-2 py-1">
              Cancelar
            </button>
            <button type="button" onClick={saveCustom}
              data-testid="operator-custom-save"
              className="bg-[#A3FF12] text-black font-black text-[11px] px-3 py-1 rounded">
              Guardar operador
            </button>
          </div>
        </div>
      )}

      {value && <OperatorBadgePreview value={value} ops={ops}/>}
    </div>
  );
};

/**
 * Réplica visual de la cabecera del PDF SAR. Muestra la esquina izquierda
 * "ADQUIRIAM · SAR-XXXXXX" y el badge del operador a la derecha, con la MISMA
 * proporción (~48mm x 14mm) que se renderiza en el PDF.
 */
const OperatorBadgePreview = ({ value, ops }) => {
  let name = 'OPERADOR';
  let bg = '#0A0A0A';
  let fg = '#FFFFFF';
  let logoUrl = '';
  if (value?.id) {
    const p = ops.find((o) => o.id === value.id);
    if (p) {
      name = p.name; bg = p.color; fg = p.text_color;
      if (p.logo_data_uri) logoUrl = p.logo_data_uri;
    }
  } else if (value?.name) {
    name = value.name.toUpperCase().slice(0, 22);
    bg = value.color || '#0A0A0A';
    fg = value.text_color || '#FFFFFF';
    if (value.logo_b64) logoUrl = `data:image/png;base64,${value.logo_b64}`;
  }
  return (
    <div className="mt-1 bg-white rounded-lg p-3 border border-white/10" data-testid="operator-badge-preview">
      <div className="text-[9px] font-black tracking-widest text-black/40 mb-1">VISTA PREVIA · CABECERA DEL PDF</div>
      <div className="flex items-center justify-between gap-3 border-b border-black/10 pb-2">
        <div>
          <div className="text-[10px] font-black tracking-widest" style={{ color: '#5A7500' }}>ADQUIRIAM</div>
          <div className="text-[10px] font-black text-black">SAR-XXXXXXXX</div>
        </div>
        {logoUrl
          ? <img src={logoUrl} alt={name} className="max-h-10 max-w-[160px] object-contain"/>
          : (
              <div
                className="px-3 py-1.5 font-black text-[11px] tracking-wide flex items-center justify-center min-w-[132px] h-[38px]"
                style={{ backgroundColor: bg, color: fg }}>
                {name}
              </div>
            )}
      </div>
      <div className="text-[9px] text-black/50 mt-1">SAR – Site Acquisition Report</div>
    </div>
  );
};

export default OperatorSelect;
